import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { useDebounce } from "../../hooks/useDebounce";
import LoadingState from "../../components/ui/loading-state";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta } from "../../components/ui/responsive-table";
import { formatTanggal } from "../../lib/tanggal";
import { Smartphone, Search, RotateCcw, ChevronRight, FileSpreadsheet, FileDown } from "lucide-react";
import { CB_BASE, CLAIM_STATUS_META, ClaimStatusPill, DeadlineBadge, PeriodeKlaimBadge, downloadBlob } from "./CashbackProgram";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

const STATUS_OPTIONS = [
  { value: "", label: "Semua Status" },
  ...Object.entries(CLAIM_STATUS_META).map(([value, m]) => ({ value, label: m.label })),
];

export default function CashbackClaim() {
  const { can } = useAuth();
  const navigate = useNavigate();

  const [statusF, setStatusF] = useState("");
  const [supplierF, setSupplierF] = useState("");
  const [programF, setProgramF] = useState("");
  const supplierDeb = useDebounce(supplierF, 400);

  const [programOpts, setProgramOpts] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_klaim: 0, total_disetujui: 0, total_settled: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);
  const [exporting, setExporting] = useState(false);

  const canView = can("finance_cashback_claim", "view");

  useEffect(() => {
    api.get(`${CB_BASE}/programs`).then((r) => setProgramOpts(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canView) return;
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const params = {};
        if (statusF) params.status = statusF;
        if (supplierDeb) params.supplier = supplierDeb;
        if (programF) params.program_id = programF;
        const r = await api.get(`${CB_BASE}/claims`, { params });
        if (batal) return;
        setItems(r.data.items || []);
        setSummary({ count: r.data.count || 0, total_klaim: r.data.total_klaim || 0, total_disetujui: r.data.total_disetujui || 0, total_settled: r.data.total_settled || 0 });
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat daftar Claim Batch"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [canView, statusF, supplierDeb, programF, reload]);

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  // `summary.count` adalah total SUNGGUHAN sebelum backend memotong ke
  // `limit` (lihat cashback_claim.py::list_claim) — beda dari kebanyakan
  // daftar lain di Finance, di sini kita TAHU PASTI kalau ada yang terpotong,
  // bukan sekadar menebak dari `shown === batas`.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    kode: (it) => it.code || "",
    program: (it) => it.program_nama || "",
    imei: (it) => it.jumlah_item || 0,
    klaim: (it) => it.total_klaim || 0,
    disetujui: (it) => it.total_disetujui || 0,
    settled: (it) => it.nominal_settled || 0,
    status: (it) => it.status || "",
  });

  const resetFilter = () => { setStatusF(""); setSupplierF(""); setProgramF(""); };
  const filterAktif = statusF || supplierF || programF;

  const exportParams = () => {
    const params = {};
    if (statusF) params.status = statusF;
    if (supplierDeb) params.supplier = supplierDeb;
    if (programF) params.program_id = programF;
    return params;
  };

  const doExportXlsx = async () => {
    setExporting(true);
    try { await downloadBlob(`${CB_BASE}/claims/export-xlsx`, "cashback-claim.xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", exportParams()); }
    catch { toast.error("Gagal mengekspor Excel"); }
    finally { setExporting(false); }
  };
  const doExportPdf = async () => {
    setExporting(true);
    try { await downloadBlob(`${CB_BASE}/claims/export-pdf`, "cashback-claim.pdf", "application/pdf", exportParams()); }
    catch { toast.error("Gagal mengekspor PDF"); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-cashback-claim">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Klaim Cashback per IMEI</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Smartphone className="w-7 h-7 text-emerald-600" /> Cashback Claim
          </h1>
          <p className="text-sm text-slate-600 mt-1">Claim Batch yang diajukan ke supplier — approval, settlement, dan rekap per batch.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={doExportXlsx} disabled={exporting} data-testid="cb-claim-export-xlsx"
                  className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={doExportPdf} disabled={exporting} data-testid="cb-claim-export-pdf"
                  className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
            <FileDown className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="cb-claim-filter-status"
                  className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[190px]">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={programF} onChange={(e) => setProgramF(e.target.value)} data-testid="cb-claim-filter-program"
                  className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[220px]">
            <option value="">Semua Program</option>
            {programOpts.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.nama_program}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={supplierF} onChange={(e) => setSupplierF(e.target.value)} placeholder="Cari supplier..." data-testid="cb-claim-filter-supplier"
                   className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none" />
          </div>
          {filterAktif && (
            <button onClick={resetFilter} data-testid="cb-claim-filter-reset"
                    className="h-10 px-3 rounded-lg text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-slate-600" data-testid="cb-claim-summary-line">
        {summary.count} Claim Batch · Total Klaim {idr(summary.total_klaim)} · Disetujui {idr(summary.total_disetujui)} · Settled <span className="font-semibold text-slate-900">{idr(summary.total_settled)}</span>
      </p>
      <BatasDaftarNotice shown={items.length} total={summary.count} />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <TabelDesktop>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label="Kode Batch" sortKeyName="kode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-claim-sort-kode" />
                <SortTh label="Program / Supplier" sortKeyName="program" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-claim-sort-program" />
                <SortTh label="Jml IMEI" sortKeyName="imei" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-claim-sort-imei" />
                <SortTh label="Total Klaim" sortKeyName="klaim" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-claim-sort-klaim" />
                <SortTh label="Disetujui" sortKeyName="disetujui" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-claim-sort-disetujui" />
                <SortTh label="Settled" sortKeyName="settled" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-claim-sort-settled" />
                <th className="px-3 py-2.5 text-left">Deadline</th>
                <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-claim-sort-status" />
                <th className="px-3 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              <LoadingState loading={loading} error={loadError} onRetry={bump} kosong={!loading && !loadError && items.length === 0}
                            pesanKosong="Belum ada Claim Batch pada filter ini" varian="tabel" kolom={9} data-testid="cb-claim-loading">
                {sortedItems.map((it) => (
                  <tr key={it.id} data-testid={`cb-claim-row-${it.id}`} onClick={() => navigate(`/finance/cashback/claims/${it.id}`)}
                      className="hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-3 py-3">
                      <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                      <p className="text-slate-800">{it.tanggal_submit ? formatTanggal(it.tanggal_submit) : "Belum disubmit"}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-slate-900">{it.program_nama}</p>
                      <p className="text-[11px] text-slate-500">{it.supplier_nama}</p>
                    </td>
                    <td className="px-3 py-3 text-center text-slate-700">{it.jumlah_item}</td>
                    <td className="px-3 py-3 text-right font-mono text-slate-800">{idr(it.total_klaim)}</td>
                    <td className="px-3 py-3 text-right font-mono text-slate-700">{idr(it.total_disetujui)}</td>
                    <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900">{idr(it.nominal_settled)}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1 items-start" data-testid={`cb-claim-deadline-${it.id}`}>
                        <DeadlineBadge deadline={it.deadline_submit_saat_dibuat} statusBatch={it.status} submittedAfterDeadline={it.submitted_after_deadline} />
                        <PeriodeKlaimBadge diLuarPeriodeKlaim={it.di_luar_periode_klaim} alasan={it.di_luar_periode_klaim_alasan} />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center"><ClaimStatusPill status={it.status} /></td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/finance/cashback/claims/${it.id}`); }}
                              data-testid={`cb-claim-open-${it.id}`} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-0.5 text-xs font-medium">
                        Detail <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="cb-claim-kartu-mobile">
          <LoadingState loading={loading} error={loadError} onRetry={bump} kosong={!loading && !loadError && items.length === 0}
                        pesanKosong="Belum ada Claim Batch pada filter ini" varian="kartu" data-testid="cb-claim-loading-m">
            {sortedItems.map((it) => (
              <KartuBaris key={it.id} testid={`cb-claim-card-${it.id}`} onClick={() => navigate(`/finance/cashback/claims/${it.id}`)}>
                <KartuBarisHeader
                  kiri={<>
                    <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                    <p className="text-sm font-semibold text-slate-900">{it.program_nama}</p>
                    <p className="text-[11px] text-slate-500">{it.supplier_nama}</p>
                  </>}
                  kanan={<ClaimStatusPill status={it.status} />}
                />
                <KartuBarisMeta label="Jumlah IMEI" value={it.jumlah_item} />
                <KartuBarisMeta label="Total Klaim" value={idr(it.total_klaim)} />
                <KartuBarisMeta label="Settled" value={idr(it.nominal_settled)} tebal />
                <div className="flex items-center justify-between pt-1">
                  <div className="flex flex-col gap-1 items-start">
                    <DeadlineBadge deadline={it.deadline_submit_saat_dibuat} statusBatch={it.status} submittedAfterDeadline={it.submitted_after_deadline} />
                    <PeriodeKlaimBadge diLuarPeriodeKlaim={it.di_luar_periode_klaim} alasan={it.di_luar_periode_klaim_alasan} />
                  </div>
                  <span className="text-[11px] text-emerald-700 font-medium inline-flex items-center gap-0.5">
                    Detail <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </KartuBaris>
            ))}
          </LoadingState>
        </DaftarKartuMobile>
      </div>
    </div>
  );
}
